import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../api/queryKeys'
import {
  fetchAllStaffDetails,
  fetchClientUserOptions,
  fetchStaffDetailsById,
  fetchStaffDataById,
  fetchStaffMonthlyBudgetById,
  updateStaffDetailsById,
  updateStaffData,
  updateStaffMonthlyBudget,
  addStaffDetails,
  deleteStaffById,
  fileToBase64,
  uploadStaffDetails,
  uploadStaffData,
  uploadStaffMonthlyBudget,
} from '../api/staff'
import type {
  StaffGridFilters,
  StaffUpdatePayload,
  StaffAddPayload,
  StaffDataUpdatePayload,
  StaffMonthlyBudgetUpdatePayload,
} from '../types/staff'

// --- Query hooks ---

export function useStaffList(clientId: string, filters: StaffGridFilters) {
  return useQuery({
    queryKey: queryKeys.staff.list(clientId, filters),
    queryFn: () => fetchAllStaffDetails(clientId, filters),
    enabled: !!clientId,
  })
}

export function useStaffClientUsers(clientId: string) {
  return useQuery({
    queryKey: queryKeys.staff.clientUsers(clientId),
    queryFn: () => fetchClientUserOptions(clientId),
    enabled: !!clientId,
  })
}

export function useStaffDetails(uuid: string, clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.staff.details(uuid),
    queryFn: () => fetchStaffDetailsById(uuid, clientId),
    enabled: !!uuid && enabled,
  })
}

export function useStaffData(uuid: string, clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.staff.data(uuid),
    queryFn: () => fetchStaffDataById(uuid, clientId),
    enabled: !!uuid && enabled,
  })
}

export function useStaffMonthlyBudget(uuid: string, clientId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.staff.monthlyBudget(uuid),
    queryFn: () => fetchStaffMonthlyBudgetById(uuid, clientId),
    enabled: !!uuid && enabled,
  })
}

// --- Mutation hooks ---

export function useUpdateStaffDetailsMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StaffUpdatePayload) => updateStaffDetailsById(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

export function useUpdateStaffDataMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StaffDataUpdatePayload) => updateStaffData(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

export function useUpdateStaffMonthlyBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StaffMonthlyBudgetUpdatePayload) => updateStaffMonthlyBudget(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

export function useAddStaffMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: StaffAddPayload) => addStaffDetails(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

export function useDeleteStaffMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ uuid, clientId }: { uuid: string; clientId: string }) =>
      deleteStaffById(uuid, clientId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.staff.all })
    },
  })
}

// --- Upload mutation hooks ---

export function useUploadStaffFilesMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      detailsFile,
      dataFile,
      clientId,
    }: {
      detailsFile?: File
      dataFile?: File
      clientId: string
    }) => {
      const errors: string[] = []
      const warnings: string[] = []

      if (detailsFile) {
        const base64 = await fileToBase64(detailsFile)
        const res = await uploadStaffDetails({ formFile: base64, contentType: 'text/csv', clientId })
        if (res.success && res.errorList?.length > 0) {
          warnings.push(...res.errorList.map((e) => 'Staff Details has been uploaded. ' + e))
        } else if (!res.success) {
          errors.push(...(res.errorList || []))
        }
      }

      if (dataFile) {
        const base64 = await fileToBase64(dataFile)
        const res = await uploadStaffData({ formFile: base64, contentType: 'text/csv', clientId })
        if (res.success && res.errorList?.length > 0) {
          warnings.push(...res.errorList.map((e) => 'Staff Data has been uploaded. ' + e))
        } else if (!res.success) {
          errors.push(...(res.errorList || []))
        }
      }

      return { errors, warnings }
    },
    onSuccess: (data) => {
      if (!data.errors.length) {
        qc.invalidateQueries({ queryKey: queryKeys.staff.all })
      }
    },
  })
}

export function useUploadMonthlyBudgetMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, clientId }: { file: File; clientId: string }) => {
      const base64 = await fileToBase64(file)
      const success = await uploadStaffMonthlyBudget({ formFile: base64, contentType: 'text/csv', clientId })
      if (!success) return { error: 'There are invalid inputs in the Monthly Budget File. Please verify the data and try again.' as string | null }
      return { error: null as string | null }
    },
    onSuccess: (data) => {
      if (!data.error) {
        qc.invalidateQueries({ queryKey: queryKeys.staff.all })
      }
    },
  })
}
